import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootStateOrAny } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { BRANDS } from '../../../types/brands';
import {
  findSpecialEventDataForDate,
  formatTime,
  weekdayNames,
  isPLKAutoCalculatedLateNight,
} from '../common/utils';

// Interface for service times containing all service times and status flags
interface ServiceTimes {
  arrivalTime?: string;
  departureTime?: string;
  drTimeOpen?: string;
  drTimeClose?: string;
  drIsClosed?: string;
  drIsOpened24Hours?: string;
  dtTimeOpen?: string;
  dtTimeClose?: string;
  dtIsClosed?: string;
  dtIsOpened24Hours?: string;
  digitalDtTimeOpen?: string;
  digitalDtTimeClose?: string;
  digitalDtIsClosed?: string;
  digitalDtIsOpened24Hours?: string;
  deliveryTimeOpen?: string;
  deliveryTimeClose?: string;
  dhIsClosed?: string;
  dhIsOpened24Hours?: string;
  cateringTimeOpen?: string;
  cateringTimeClose?: string;
  cateringIsClosed?: string;
  curbsideTimeOpen?: string;
  curbsideTimeClose?: string;
  curbsideIsClosed?: string;
}

// Interface for base week hours data structure
interface BaseWeekHours {
  [dayKey: string]: ServiceTimes;
}

// Interface for special event data
interface SpecialEventData extends ServiceTimes {
  date_value: string;
  eventDate?: string;
  eventName?: string;
  specialEventReason?: number | string;
}

// Interface for restaurant service
interface RestaurantService {
  code: string;
  value: boolean;
  name?: string;
}

// Interface for restaurant entity from Redux
interface RestaurantEntity {
  RestaurantNumber?: string;
  brand: BRANDS;
  Region: string;
}

// Interface for service data returned by getServiceData
interface ServiceData {
  na?: boolean;
  closed?: boolean;
  twentyFourHour?: boolean;
  time?: string;
  baseTime?: string; // Base hours time for strikethrough display
  hasSpecialEvent?: boolean; // Flag to indicate if special event data exists
}

// Interface for enhanced service display data
interface ServiceDisplayData extends ServiceData {
  displayText: string;
  cardClass: 'dayCardGreen' | 'dayCardRed' | 'dayCardGray';
  isRecentlyEdited: boolean;
  specialEventClassName: string;
  strikethroughText: string;
}

// Interface for service object in services array
interface Service {
  key: string;
  label: string;
}

// Interface for recently edited field value
interface EditedFieldValue {
  current: string | undefined;
}

// Interface for recently edited base hours by weekday
interface RecentlyEditedDayData {
  [fieldName: string]: EditedFieldValue;
}

// Interface for recently edited base hours structure
interface RecentlyEditedBaseHours {
  [weekdayName: string]: RecentlyEditedDayData;
}

// Interface for extracted field values (after extracting current values)
interface ExtractedFieldValues {
  [fieldName: string]: string | undefined;
}

interface UseServiceDataProps {
  weekDays: Date[];
  baseWeekHours: BaseWeekHours;
  specialEventDayByDay: SpecialEventData[];
  is12HFormat: boolean;
  recentlyEditedBaseHours?: RecentlyEditedBaseHours;
}

// Type for translation function from react-i18next
type TranslationFunction = (key: string) => string;

export const useServiceData = ({
  weekDays,
  baseWeekHours,
  specialEventDayByDay,
  is12HFormat,
  recentlyEditedBaseHours = {},
}: UseServiceDataProps) => {
  const { t } = useTranslation();

  // Get restaurant data from Redux store
  const {
    RestaurantNumber: restaurantNumber,
    brand,
    Region,
  } = (useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.entity,
  ) as RestaurantEntity) || {};

  // Get restaurant services from Redux store
  const restaurantServices = useSelector(
    (state: RootStateOrAny) => state.ServicesReducer.entities,
  ) as RestaurantService[];

  // Get service availability based on restaurant services
  const curbsideService = restaurantServices.find(
    elem => elem.code === 'curbsidePickup',
  );
  const cateringService = restaurantServices.find(
    elem => elem.code === 'catering',
  );
  const driveThruService = restaurantServices.find(
    elem => elem.code === 'driveThru',
  );
  const digitalDriveThruService = restaurantServices.find(
    elem => elem.code === 'digitalDriveThru',
  );

  const countries = useSelector(
    (state: RootStateOrAny) =>
      state.ReportingUnitsReducer?.reportingUnitsCountries,
  );

  // Dynamic services based on BaseAdjustment logic
  const services = useMemo((): Service[] => {
    const serviceList: Service[] = [
      { key: 'storeArrival', label: t('operationHours.storeArrival') },
    ];

    // Add store departure only for TH brand in NA region
    if (brand === BRANDS.TH && (Region === 'NA' || !restaurantNumber)) {
      serviceList.push({
        key: 'storeDeparture',
        label: t('operationHours.storeDeparture'),
      });
    }

    // Add dining room
    serviceList.push({
      key: 'diningRoom',
      label: t('operationHours.diningRoom'),
    });

    // Add drive thru if service is available
    if (driveThruService && driveThruService.value) {
      serviceList.push({
        key: 'driveThru',
        label: t('operationHours.driveThru'),
      });
    }

    // Add digital drive thru if service is available and conditions are met
    if (
      brand === BRANDS.BK &&
      (Region === 'NA' || !restaurantNumber) &&
      digitalDriveThruService &&
      digitalDriveThruService.value
    ) {
      serviceList.push({
        key: 'mobileOrderDriveThru',
        label: t('operationHours.mobileOrderDriveThru'),
      });
    }

    // Add delivery time
    serviceList.push({
      key: 'deliveryTime',
      label: t('operationHours.deliveryTime'),
    });

    // Add catering if service is available
    if (
      cateringService &&
      cateringService.value &&
      (brand !== BRANDS.PLK ||
        !isPLKAutoCalculatedLateNight(brand, Region, countries))
    ) {
      serviceList.push({
        key: 'cateringTime',
        label: t('operationHours.cateringTime'),
      });
    }

    // Add curbside if service is available
    if (curbsideService && curbsideService.value) {
      serviceList.push({
        key: 'curbsideTime',
        label: t('operationHours.curbsideTime'),
      });
    }

    return serviceList;
  }, [
    brand,
    Region,
    restaurantNumber,
    driveThruService,
    digitalDriveThruService,
    cateringService,
    curbsideService,
    t,
  ]);

  // Get service data
  const getServiceData = (dayIndex: number, service: string): ServiceData => {
    // Map Monday-Sunday display order to the baseWeekHours format (1-Sunday, 2-Monday, etc.)
    const dayNumber = dayIndex === 6 ? 1 : dayIndex + 2; // Sunday (index 6) is 1, Monday (index 0) is 2, etc.
    const dayKey = `${dayNumber} - ${['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][dayIndex]}`;
    const serviceTimes = baseWeekHours[dayKey];

    if (!serviceTimes) {
      return { na: true };
    }

    const serviceObj = services.find(s => s.label === service);
    if (!serviceObj) {
      return { na: true };
    }

    const serviceKey = serviceObj.key;

    // Check if special event data exists for this day
    let dataToUse: ServiceTimes = serviceTimes;
    let hasSpecialEvent = false;
    let baseTime: string | undefined;

    if (specialEventDayByDay && weekDays) {
      const specialEventData = findSpecialEventDataForDate(
        specialEventDayByDay,
        weekDays[dayIndex],
      );
      if (specialEventData) {
        hasSpecialEvent = true;
        // Get base hours time for strikethrough display
        const baseServiceData = processServiceTimes(
          serviceTimes,
          serviceKey,
          is12HFormat,
        );
        baseTime = baseServiceData.time || undefined;
        dataToUse = specialEventData as ServiceTimes;
      }
    }

    // Common properties for all return statements
    const commonProps = {
      hasSpecialEvent,
      baseTime,
    };

    return processServiceTimes(dataToUse, serviceKey, is12HFormat, commonProps);
  };

  // Get comprehensive service display information
  const getServiceDisplayInfo = (
    serviceLabel: string,
    dayIndex: number,
  ): ServiceDisplayData => {
    // Early return for invalid date
    if (!weekDays[dayIndex]) {
      return DEFAULT_SERVICE_DISPLAY_DATA;
    }

    // Retrieve special and base hours
    const serviceData = getServiceData(dayIndex, serviceLabel);

    // Find the service object to get the stable key
    const serviceObj = services.find(s => s.label === serviceLabel);
    if (!serviceObj) {
      return DEFAULT_SERVICE_DISPLAY_DATA;
    }

    // Format service data text
    const serviceDataText = formatServiceDataText(serviceData, t);

    // Get recently edited base hours for this specific service and day
    // Pass baseWeekHours to fill in missing time fields (e.g., when only open time is edited)
    const recentlyEditedBaseHoursRawData = extractRecentlyEditedBaseHours({
      serviceKey: serviceObj.key, // Use stable key instead of translated label
      dayIndex,
      weekDays,
      recentlyEditedBaseHours,
      baseWeekHours,
    });
    // Process raw data into ServiceData structure
    const recentlyEditedBaseHoursData = recentlyEditedBaseHoursRawData
      ? processRecentlyEditedBaseHours(
          recentlyEditedBaseHoursRawData,
          serviceObj.key,
          is12HFormat,
        )
      : null;

    const recentlyEditedBaseHoursText = recentlyEditedBaseHoursData
      ? formatServiceDataText(recentlyEditedBaseHoursData, t)
      : '';

    return {
      ...serviceData,
      displayText: getDisplayText({
        hasSpecialEvent: !!serviceData.hasSpecialEvent,
        serviceDataText,
        recentlyEditedBaseHoursText,
      }),
      cardClass: getCardClass(recentlyEditedBaseHoursData, serviceData),
      isRecentlyEdited: !!recentlyEditedBaseHoursText,
      specialEventClassName: serviceData.hasSpecialEvent
        ? 'specialEventTime'
        : '',
      strikethroughText: getStrikethroughText({
        hasSpecialEvent: !!serviceData.hasSpecialEvent,
        baseTime: serviceData.baseTime,
        recentlyEditedBaseHoursText,
      }),
    };
  };

  return {
    services,
    getServiceData,
    getServiceDisplayInfo,
  };
};

export type {
  ServiceData,
  ServiceDisplayData,
  Service,
  ServiceTimes,
  BaseWeekHours,
  SpecialEventData,
  RecentlyEditedBaseHours,
  ExtractedFieldValues,
  EditedFieldValue,
  RecentlyEditedDayData,
};

// Service configuration constants
const SERVICE_CONFIG = {
  singleTimeFields: {
    storeArrival: 'arrivalTime',
    storeDeparture: 'departureTime',
  },
  timeRangeFields: {
    diningRoom: {
      prefix: 'dr',
      has24Hour: true,
      timeOpen: 'drTimeOpen',
      timeClose: 'drTimeClose',
    },
    driveThru: {
      prefix: 'dt',
      has24Hour: true,
      timeOpen: 'dtTimeOpen',
      timeClose: 'dtTimeClose',
    },
    mobileOrderDriveThru: {
      prefix: 'digitalDt',
      has24Hour: true,
      timeOpen: 'digitalDtTimeOpen',
      timeClose: 'digitalDtTimeClose',
    },
    deliveryTime: {
      prefix: 'dh',
      has24Hour: true,
      timeOpen: 'deliveryTimeOpen',
      timeClose: 'deliveryTimeClose',
    },
    curbsideTime: {
      prefix: 'curbside',
      has24Hour: false,
      timeOpen: 'curbsideTimeOpen',
      timeClose: 'curbsideTimeClose',
    },
    cateringTime: {
      prefix: 'catering',
      has24Hour: false,
      timeOpen: 'cateringTimeOpen',
      timeClose: 'cateringTimeClose',
    },
  },
} as const;

// Default return object for invalid service display info
const DEFAULT_SERVICE_DISPLAY_DATA: ServiceDisplayData = {
  displayText: '',
  cardClass: 'dayCardGray',
  isRecentlyEdited: false,
  specialEventClassName: '',
  strikethroughText: '',
  na: true,
};

/**
 * Extract recently edited base hours for a specific service and day
 * Merges edited fields with existing base hours to create complete time data
 * @param params - Object containing extraction parameters
 * @param params.serviceKey - Service key to check for updates
 * @param params.dayIndex - Day index in the week
 * @param params.weekDays - Array of Date objects for the week
 * @param params.recentlyEditedBaseHours - Recently edited base hours data from Redux
 * @param params.baseWeekHours - Base week hours to fill in missing time fields
 * @returns Recently edited base hours object merged with base hours, or null if no updates
 */
function extractRecentlyEditedBaseHours({
  serviceKey,
  dayIndex,
  weekDays,
  recentlyEditedBaseHours,
  baseWeekHours,
}: {
  serviceKey: string;
  dayIndex: number;
  weekDays: Date[];
  recentlyEditedBaseHours: RecentlyEditedBaseHours;
  baseWeekHours: BaseWeekHours;
}): ExtractedFieldValues | null {
  const fieldsToCheck = getServiceFieldsToCheck(serviceKey);
  const currentDate = weekDays[dayIndex];

  if (!currentDate) return null;

  const dayOfWeek = currentDate.getDay();
  const weekdayKey = weekdayNames[dayOfWeek];
  const dayUpdates = recentlyEditedBaseHours[weekdayKey];

  if (!dayUpdates) return null;

  // Check if any of the service fields were edited
  const hasEditedFields = fieldsToCheck.some(field => dayUpdates[field]);
  if (!hasEditedFields) return null;

  // Get base hours for this day to fill in missing time fields
  const baseServiceTimes = baseWeekHours[weekdayKey];

  const extractedUpdates: ExtractedFieldValues = {};

  // Get the time field config for this service to know which fields to merge
  const timeConfig =
    SERVICE_CONFIG.timeRangeFields[
      serviceKey as keyof typeof SERVICE_CONFIG.timeRangeFields
    ];

  fieldsToCheck.forEach(field => {
    if (dayUpdates[field]) {
      // Use the edited value
      extractedUpdates[field] = dayUpdates[field].current;
    } else if (timeConfig && baseServiceTimes) {
      // For time range services, fill in missing open/close time from base hours
      // This ensures we show the complete time range when only one field was edited
      if (field === timeConfig.timeOpen || field === timeConfig.timeClose) {
        const baseValue = baseServiceTimes[field as keyof ServiceTimes];
        if (baseValue) {
          extractedUpdates[field] = baseValue;
        }
      }
    }
  });

  return Object.keys(extractedUpdates).length > 0 ? extractedUpdates : null;
}

/**
 * Process service times data into ServiceData structure
 * @param serviceTimes - Service times data (from base hours or recently edited)
 * @param serviceKey - Service key to determine processing logic
 * @param is12HFormat - Time format preference
 * @param additionalProps - Additional properties to include in result
 * @returns Processed ServiceData structure
 */
function processServiceTimes(
  serviceTimes: ServiceTimes,
  serviceKey: string,
  is12HFormat: boolean,
  additionalProps: Partial<ServiceData> = {},
): ServiceData {
  // Handle single time field services
  const singleTimeField =
    SERVICE_CONFIG.singleTimeFields[
      serviceKey as keyof typeof SERVICE_CONFIG.singleTimeFields
    ];
  if (singleTimeField && serviceTimes[singleTimeField as keyof ServiceTimes]) {
    const timeValue = serviceTimes[singleTimeField as keyof ServiceTimes];
    return {
      time: formatTime(timeValue as string, is12HFormat) || '',
      twentyFourHour: false,
      ...additionalProps,
    };
  }

  // Handle services with time ranges and status flags
  const timeFields = getServiceTimeFieldsByKey(serviceKey);
  if (!timeFields) {
    return { na: true, ...additionalProps };
  }

  // Check if service is closed
  if (
    timeFields.isClosed &&
    serviceTimes[timeFields.isClosed as keyof ServiceTimes] === 'Y'
  ) {
    return { closed: true, ...additionalProps };
  }

  // Check if service is 24 hours
  if (
    'isOpened24Hours' in timeFields &&
    timeFields.isOpened24Hours &&
    serviceTimes[timeFields.isOpened24Hours as keyof ServiceTimes] === 'Y'
  ) {
    return { twentyFourHour: true, ...additionalProps };
  }

  // Format time range if both open and close times are available
  const openTime = serviceTimes[timeFields.timeOpen];
  const closeTime = serviceTimes[timeFields.timeClose];

  if (!openTime && !closeTime) {
    return { na: true, ...additionalProps };
  }

  const formattedOpen = openTime ? formatTime(openTime, is12HFormat) : null;
  const formattedClose = closeTime ? formatTime(closeTime, is12HFormat) : null;

  if (formattedOpen && formattedClose) {
    return {
      time: `${formattedOpen} - ${formattedClose}`,
      twentyFourHour: false,
      ...additionalProps,
    };
  } else if (formattedOpen) {
    return {
      time: `${formattedOpen}`,
      twentyFourHour: false,
      ...additionalProps,
    };
  } else if (formattedClose) {
    return {
      time: `${formattedClose}`,
      twentyFourHour: false,
      ...additionalProps,
    };
  }

  return { na: true, ...additionalProps };
}

/**
 * Process recently edited base hours data into ServiceData structure
 * @param rawData - Raw field data from recently edited base hours
 * @param serviceKey - Service key to determine processing logic
 * @param is12HFormat - Time format preference
 * @returns Processed ServiceData structure
 */
function processRecentlyEditedBaseHours(
  rawData: ExtractedFieldValues,
  serviceKey: string,
  is12HFormat: boolean,
): ServiceData {
  return processServiceTimes(rawData as ServiceTimes, serviceKey, is12HFormat);
}

/**
 * Get fields to check for recently edited base hours based on service key
 * @param serviceKey - Stable service key (not translated label)
 * @returns Array of field names to check
 */
function getServiceFieldsToCheck(serviceKey: string): string[] {
  // Handle single time field services
  const singleTimeField =
    SERVICE_CONFIG.singleTimeFields[
      serviceKey as keyof typeof SERVICE_CONFIG.singleTimeFields
    ];
  if (singleTimeField) {
    return [singleTimeField];
  }

  // Handle time range services
  const config =
    SERVICE_CONFIG.timeRangeFields[
      serviceKey as keyof typeof SERVICE_CONFIG.timeRangeFields
    ];
  if (!config) {
    return [];
  }

  const fields = [
    config.timeOpen,
    config.timeClose,
    `${config.prefix}IsClosed`,
  ];

  if (config.has24Hour) {
    fields.push(`${config.prefix}IsOpened24Hours`);
  }

  return fields;
}

/**
 * Get time field mappings for a service key (stable key, not translated label)
 * @param serviceKey - Service key to get time fields for
 * @returns Time field mapping object or null
 */
function getServiceTimeFieldsByKey(serviceKey: string) {
  const config =
    SERVICE_CONFIG.timeRangeFields[
      serviceKey as keyof typeof SERVICE_CONFIG.timeRangeFields
    ];
  if (!config) {
    return null;
  }

  return {
    timeOpen: config.timeOpen,
    timeClose: config.timeClose,
    isClosed: `${config.prefix}IsClosed`,
    ...(config.has24Hour && {
      isOpened24Hours: `${config.prefix}IsOpened24Hours`,
    }),
  };
}

// Format service data into display text
function formatServiceDataText(
  serviceData: ServiceData,
  t: TranslationFunction,
): string {
  if (serviceData.closed) return t('operationHours.closed');
  if (serviceData.na) return t('app.common.na');
  if (serviceData.twentyFourHour) return t('operationHours.24Hour');
  return serviceData.time || '';
}

// Get display text with proper priority: Special Event > Recently Edited > Service Data
function getDisplayText({
  hasSpecialEvent,
  serviceDataText,
  recentlyEditedBaseHoursText,
}: {
  hasSpecialEvent: boolean;
  serviceDataText: string;
  recentlyEditedBaseHoursText: string;
}): string {
  if (hasSpecialEvent) {
    return serviceDataText; // Special event takes priority
  }
  return recentlyEditedBaseHoursText || serviceDataText; // Recently edited or fallback to service data
}

// Get strikethrough text for special events
function getStrikethroughText({
  hasSpecialEvent,
  baseTime,
  recentlyEditedBaseHoursText,
}: {
  hasSpecialEvent: boolean;
  baseTime: string | undefined;
  recentlyEditedBaseHoursText: string;
}): string {
  if (!hasSpecialEvent) {
    return ''; // No strikethrough when no special event
  }
  return recentlyEditedBaseHoursText || baseTime || '';
}

// Get card class based on service data
function getCardClass(
  serviceEditedBaseHours: ServiceData | null,
  serviceData: ServiceData,
): 'dayCardGreen' | 'dayCardRed' | 'dayCardGray' {
  const dataToCheck = serviceEditedBaseHours || serviceData;

  if (dataToCheck.closed) return 'dayCardRed';
  if (dataToCheck.na) return 'dayCardGray';
  return 'dayCardGreen';
}
